import { useDownloadsNotification } from '@common/hooks';
import {
  useCancelReportGeneratingMutation,
  useDeleteReportDownloadTableMutation,
  useReportsDownloadQuery,
  useRetryReportGeneratingMutation,
  useTableDownloadReportMutation,
} from '@common/cache';
import { toast } from 'react-toastify';
import { formatApiError } from '@common/errors/error-formatting.ts';
import {
  IDownloadsTableEntry,
  IDownloadsTableFilters,
} from '@views/pages/Downloads/hooks/useDownloads.ts';
import { toNumber } from 'lodash';
import { useEffect, useState } from 'react';
import { DownloadsReportEventsType } from '@common/types';
import { debounce } from '@util/debounce.ts';

export type ColumnNames =
  'id' | 'fileName' | 'status' | 'generatedOn' | 'action';

const initialFilters: IDownloadsTableFilters = {
  page: 1,
  limit: 100,
};

const handledEventList: DownloadsReportEventsType[] = [
  'report-download.progress',
  'report-download.updated',
  'report-download.completed',
];

export const useReportsDownload = ({
  handleCloseDeleteModal,
  setIsDeleteButtonDisabled,
  setSelectedItems,
  selectedItems,
}: {
  handleCloseDeleteModal: () => void;
  setIsDeleteButtonDisabled: (isDisabled: boolean) => void;
  setSelectedItems: (selectedItems: IDownloadsTableEntry[]) => void;
  selectedItems: IDownloadsTableEntry[];
}) => {
  const { event } = useDownloadsNotification();
  const [entries, setEntries] = useState<IDownloadsTableEntry[]>([]);
  const [filters, setFilters] =
    useState<IDownloadsTableFilters>(initialFilters);
  const [reportsCurrentDownloadItem, setCurrentDownloadItem] =
    useState<IDownloadsTableEntry | null>(null);

  const {
    data = { report_downloads: null, count: 0 },
    isPending,
    refetch,
  } = useReportsDownloadQuery(filters);

  const { mutateAsync: downloadAsync, isPending: isDownloading } =
    useTableDownloadReportMutation();

  const { mutateAsync: deleteAsync, isPending: isDeleting } =
    useDeleteReportDownloadTableMutation();

  const { mutateAsync: cancelAsync, isPending: isCanceling } =
    useCancelReportGeneratingMutation();

  const { mutateAsync: retryAsync, isPending: isRetrying } =
    useRetryReportGeneratingMutation();

  const handleDownload = async (row: IDownloadsTableEntry) => {
    setCurrentDownloadItem(row);

    try {
      await downloadAsync({
        id: row.id,
        report_type: row?.options?.report_type || 'patients_report',
        report_name: row.name,
        timestamp: row.generated_at,
      });
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCurrentDownloadItem(null);
    }
  };

  const handleCancel = async (row: IDownloadsTableEntry) => {
    setCurrentDownloadItem(row);

    try {
      await cancelAsync(row.id);
      toast.success('Cancelled');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCurrentDownloadItem(null);
    }
  };

  const handleRetry = async (row: IDownloadsTableEntry) => {
    setCurrentDownloadItem(row);

    try {
      await retryAsync(row.id);
      toast.success('Retrying');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setCurrentDownloadItem(null);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAsync(
        entries.filter(item => item.checked).map(entry => entry.id),
      );

      setFilters(initialFilters);
      handleCloseDeleteModal();
      toast.success('Deleted');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const setRowsSelection = (selectedIds: Set<string>) => {
    const next = entries.map(item => ({
      ...item,
      checked: selectedIds.has(item.id),
    }));
    setEntries(next);
    setSelectedItems(next);
  };

  const onPageIndexChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    setFilters({ ...filters, page: page + 1 });
  };

  const onPageSizeChange = (event: {
    target: {
      value: string;
    };
  }) => {
    setFilters({ ...filters, limit: toNumber(event.target.value), page: 1 });
  };

  const onSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    debounce(
      () => setFilters({ ...filters, search: newValue, page: 1 }),
      true,
      500,
    );
  };

  useEffect(() => {
    if (data.report_downloads) {
      setEntries(
        data.report_downloads.map(item => ({
          ...item,
          checked: !!selectedItems.find(entry => entry.id === item.id)?.checked,
          id: item.id,
          name: item.report_name_at_creation,
          generated_at: item.generated_at,
          options: {
            report_type: item.report_type,
          },
        })),
      );
    }
  }, [data]);

  useEffect(() => {
    if (event && handledEventList.includes(event.type)) {
      setEntries(prevState =>
        prevState.map(item => ({
          ...item,
          ...(event.payload.report_download_id === item.id &&
            event.type === 'report-download.progress' && {
              download_percentage: event.payload?.percentage || 0,
            }),
          ...(event.payload.report_download_id === item.id &&
            (event.type === 'report-download.updated' ||
              event.type === 'report-download.completed') && {
              status: event.payload?.status || item.status,
            }),
          ...(event.payload.report_download_id === item.id &&
            event.type === 'report-download.completed' && {
              generated_at: event.payload?.generated_at || item.generated_at,
              file_size: event.payload?.file_size || null,
            }),
        })),
      );
      if (event.type === 'report-download.completed') {
        refetch();
      }
    }
  }, [event]);

  useEffect(() => {
    setIsDeleteButtonDisabled(isDownloading || isCanceling || isDeleting);
  }, [isDownloading, isCanceling, isDeleting, entries]);

  return {
    filters,
    onPageIndexChange,
    onPageSizeChange,
    onSearchChange,
    entries,
    setRowsSelection,
    isLoading: isPending,
    handleDownload,
    reportsCurrentDownloadItem,
    disableDeleteButton:
      isDownloading ||
      isCanceling ||
      isRetrying ||
      entries.every(entry => !entry.checked) ||
      isDeleting,
    handleDelete,
    handleCancel,
    handleRetry,
    totalCount: data.count,
    isDeleting,
  };
};
